import {
  useDeleteMutation,
  useGetQuery,
  useUpdateMutation,
} from '@gripp/shared-logic';
import {
  Colors,
  ControlledTextInput,
  CustomAlert,
  SaveFormTop,
  TopModalBar,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAudioPlayer } from 'expo-audio';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import Toast from 'react-native-toast-message';
import * as yup from 'yup';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const audioSource = require('../../../static-assets/delete_pop.mp3');

type EditAlertFormProps = {
  onComplete: () => void;
  workspaceId: any;
  alertId: string;
};

export const EditAlertForm = ({
  workspaceId,
  onComplete,
  alertId,
}: EditAlertFormProps) => {
  const { t } = useTranslation();
  const translationPath = 'alertsManagement.deleteAlert';

  const { data, isLoading: isNameLoading } = useGetQuery({
    modelName: 'workflow',
    query: GET_WORKFLOW,
    id: alertId,
  });

  const [alertName, setAlertName] = useState('');
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  const player = useAudioPlayer(audioSource);

  const { mutateAsync, isPending } = useUpdateMutation({
    modelName: 'workflow',
    query: UPDATE_WORKFLOW,
  });

  const { mutateAsync: deleteAlertAsync, isPending: isDeleteAlertPending } =
    useDeleteMutation({
      modelName: 'workflow',
      query: DELETE_WORKFLOW,
    });

  const formSchema = yup.object({
    name: yup.string().required('Alert name is required'),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: { name: alertName },
  });

  useEffect(() => {
    if (!data) return;

    reset({ name: data.name });
    setAlertName(data.name);
  }, [data, reset]);

  const onSubmit = async (data: yup.InferType<typeof formSchema>) => {
    const input = {
      id: alertId,
      name: data.name,
      workspace: { id: workspaceId?.id },
    };

    await mutateAsync(
      { input },
      {
        onSettled: () => {
          onComplete();
        },
      }
    );
  };

  const onAlertDelete = async () => {
    const input = {
      id: alertId,
      workspace: { id: workspaceId?.id },
    };

    await deleteAlertAsync({ input });

    onComplete();
    showToast();
    player.play();
  };

  const showToast = () => {
    Toast.show({
      type: 'blackToast',
      text1: 'Woosh!',
      text2: `${alertName} has been deleted.`,
      position: 'bottom',
      autoHide: true,
      visibilityTime: 2500,
    });
  };

  return (
    !isNameLoading && (
      <>
        <View style={{ flex: 1, width: '100%' }}>
          <SaveFormTop
            onSubmit={handleSubmit(onSubmit)}
            title={alertName}
            topBar={TopModalBar}
            isDirty={isDirty}
            onCancel={onComplete}
          >
            <View style={styles.innerFormContainer}>
              <ControlledTextInput
                control={control}
                name="name"
                placeholder="Alert Name"
              />
            </View>
            <View style={styles.bottomContainer}>
              <Pressable
                style={styles.deleteButton}
                onPress={() => {
                  setShowDeleteAlert(true);
                }}
              >
                <Text style={styles.deleteButtonLabel}>
                  {t(`${translationPath}.deleteAlert`)}
                </Text>
              </Pressable>
            </View>
          </SaveFormTop>
        </View>
        <CustomAlert
          visible={showDeleteAlert}
          hideAlert={() => setShowDeleteAlert(false)}
          title={
            <>
              {t(`${translationPath}.alertBody1`)} <b>{alertName}</b>? <br />
              <br />
              <b>{t(`${translationPath}.alertBody2`)}</b>
              {t(`${translationPath}.alertBody3`)}
            </>
          }
          primaryButtonLabel={t(`${translationPath}.delete`)}
          secondaryButtonLabel={t(`${translationPath}.cancel`)}
          action={() => onAlertDelete()}
          containerStyle={styles.deleteAlertContainer}
        />
      </>
    )
  );
};

const styles = StyleSheet.create({
  innerFormContainer: {
    margin: 20,
  },
  bottomContainer: {
    flex: 1,
    borderTopWidth: 1,
    borderTopColor: Colors.role.borderControl,
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 20,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  deleteButtonLabel: {
    fontSize: 16,
    fontWeight: '600',
    letterSpacing: -0.25,
    color: Colors.danger,
  },
  deleteAlertContainer: {
    width: 350,
  },
});

const UPDATE_WORKFLOW = `
  mutation UpdateWorkflow($input: MutateWorkflowInput!) {
    updateWorkflow(input: $input) {
      id
      name
      workspace {
        id
      }
    }
  }
`;

const DELETE_WORKFLOW = `
  mutation DeleteWorkflow($input: MutateWorkflowInput!) {
    deleteWorkflow(input: $input) {
      id
    }
  }
`;

const GET_WORKFLOW = `
  query GetWorkflow($id: ID!) {
    getWorkflow(id: $id) {
      id
      name
      workspace {
        id
      }
    }
  }
`;
